// Swipe Card Component
// Individual card with flip functionality and gesture handling

import React, { useMemo, useRef, useEffect, useCallback } from 'react';
import { motion, useMotionValue, useTransform, animate, PanInfo } from 'framer-motion';
import { SwipeCardProps } from '../../types/swipe';
import { CardTeaser } from './CardTeaser';
import { CardRevealed } from './CardRevealed';
import {
  SwipeCardContainer,
  CardFace,
  CardSuitCorner,
  CardSuitSymbol,
  SwipeVerdictStamp
} from '../../styles/Swipe/Swipe.styles';

// Playing card suits (just symbols, no letters)
const SUITS = [
  { symbol: '♠', color: '#000000', name: 'spades' },
  { symbol: '♥', color: '#DC143C', name: 'hearts' },
  { symbol: '♦', color: '#DC143C', name: 'diamonds' },
  { symbol: '♣', color: '#000000', name: 'clubs' }
];

// Gesture thresholds. A drag commits when it travels far enough, OR when it is
// short but fast — a flick is a decision too.
const COMMIT_DISTANCE = 110; // px of travel that commits the swipe
const FLICK_VELOCITY = 500;  // px/s that commits a shorter drag
const FLICK_DISTANCE = 50;   // px a flick must still cover, so a tap can't fire one
const TAP_SLOP = 8;          // px of movement still counted as a tap, not a drag
const FLY_OFF_DISTANCE = 600; // px the card travels on its way off screen

export const SwipeCard: React.FC<SwipeCardProps> = ({
  card,
  isTopCard,
  stackIndex = 0,
  onSwipe,
  onLongPress,
  onFlip,
  settings,
  zIndex
}) => {
  // Generate consistent random suit based on user name
  const cardSuit = useMemo(() => {
    const hash = card.user.name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return SUITS[hash % SUITS.length];
  }, [card.user.name]);

  // Motion values for the card itself. These are owned here rather than left to
  // framer's internals so the drag, the fly-off and the verdict stamps all read
  // from the same position.
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  // The card leans into the direction it is being thrown.
  const rotate = useTransform(x, [-300, 0, 300], [-24, 0, 24]);
  // Stamps fade in with travel and hit full opacity right at the commit point,
  // so "fully lit" reads as "let go now and this happens".
  const keepStampOpacity = useTransform(x, [30, COMMIT_DISTANCE], [0, 1]);
  const passStampOpacity = useTransform(x, [-COMMIT_DISTANCE, -30], [1, 0]);

  const didDragRef = useRef(false);
  // A committed drag hands the decision to the parent, which may refuse it (a
  // full keep pile does). This timer brings the card home if no swipe comes
  // back, so a refused gesture can't strand the card off-centre.
  const rejectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const canInteract = isTopCard && !card.isAnimating;
  const swipeDuration = (settings?.swipeAnimationDuration || 300) / 1000;

  const springHome = useCallback(() => {
    animate(x, 0, { type: 'spring', stiffness: 300, damping: 30 });
    animate(y, 0, { type: 'spring', stiffness: 300, damping: 30 });
  }, [x, y]);

  // Fly the card off in the direction it was sent. This covers every route to a
  // swipe — the drag, the Deal button, and the Pass/Keep buttons on the back
  // face — because they all land on the same `swipeDirection` state.
  useEffect(() => {
    if (!card.swipeDirection || !card.isAnimating) return;

    if (rejectTimerRef.current) {
      clearTimeout(rejectTimerRef.current);
      rejectTimerRef.current = null;
    }

    const transition = { duration: swipeDuration, ease: 'easeOut' as const };
    switch (card.swipeDirection) {
      case 'left':
        animate(x, -FLY_OFF_DISTANCE, transition);
        break;
      case 'right':
        animate(x, FLY_OFF_DISTANCE, transition);
        break;
      case 'up':
        animate(y, -FLY_OFF_DISTANCE, transition);
        break;
      case 'down':
        animate(y, FLY_OFF_DISTANCE, transition);
        break;
    }
  }, [card.swipeDirection, card.isAnimating, swipeDuration, x, y]);

  // A card that is neither dragged nor animating belongs at rest in the centre.
  useEffect(() => {
    if (card.swipeDirection || card.isAnimating) return;
    x.set(0);
    y.set(0);
  }, [card.id, card.swipeDirection, card.isAnimating, x, y]);

  useEffect(() => () => {
    if (rejectTimerRef.current) clearTimeout(rejectTimerRef.current);
  }, []);

  // Handle card tap/click to flip. A drag that ends over the card also fires a
  // click, so a gesture that moved is not allowed to flip on the way out.
  const handlePointerDown = () => {
    didDragRef.current = false;
  };

  const handleCardClick = (e: React.MouseEvent) => {
    if (didDragRef.current) {
      didDragRef.current = false;
      return;
    }
    if (!canInteract || !onFlip) return;

    e.preventDefault();
    e.stopPropagation();
    onFlip();
  };

  const handleDrag = (_e: unknown, info: PanInfo) => {
    if (Math.abs(info.offset.x) > TAP_SLOP) {
      didDragRef.current = true;
    }
  };

  const handleDragEnd = (_e: unknown, info: PanInfo) => {
    const offsetX = info.offset.x;
    const velocityX = info.velocity.x;
    const committed =
      Math.abs(offsetX) > COMMIT_DISTANCE ||
      (Math.abs(velocityX) > FLICK_VELOCITY && Math.abs(offsetX) > FLICK_DISTANCE);

    if (!committed) {
      springHome();
      return;
    }

    // Right keeps, left passes — the same two outcomes as the buttons on the
    // back of the card, so the gesture and the buttons stay one vocabulary.
    onSwipe(offsetX > 0 ? 'right' : 'left');

    // Hold position for a beat; the fly-off effect takes over as soon as the
    // parent accepts. If it never does, come home.
    if (rejectTimerRef.current) clearTimeout(rejectTimerRef.current);
    rejectTimerRef.current = setTimeout(() => {
      rejectTimerRef.current = null;
      springHome();
    }, 250);
  };

  return (
    <motion.div
      drag={canInteract ? 'x' : false}
      dragElastic={0.55}
      dragMomentum={false}
      onPointerDown={handlePointerDown}
      onDrag={handleDrag}
      onDragEnd={handleDragEnd}
      exit={{
        opacity: 0,
        scale: 0.8,
        transition: { duration: 0.2 }
      }}
      style={{
        zIndex,
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        x,
        y,
        rotate,
        // Composite the card instead of repainting it every frame. It carries a
        // large box-shadow, a rounded mask and often a playing video, all of
        // which are expensive to repaint on a phone — and a drag repaints on
        // every touchmove.
        willChange: 'transform',
        // Only the top card is being dragged; the ones underneath do not need
        // to react to touch at all, and letting them try is work for nothing.
        pointerEvents: isTopCard ? 'auto' : 'none'
      }}
    >
      <SwipeCardContainer
        $zIndex={zIndex || 0}
        $isTopCard={isTopCard}
        $isAnimating={card.isAnimating || false}
        onClick={handleCardClick}
        style={{
          // The cursor is left to the styled component's grab/grabbing pair —
          // an inline value here would outrank its :active rule.
          //
          // TOUCH-ACTION IS THE SMOOTHNESS. With 'pan-y' the browser has to
          // watch the first few pixels of every gesture to decide whether the
          // user meant to scroll vertically, and it withholds pointer events
          // until it knows — which is felt as the card lagging behind the
          // thumb before it catches up.
          //
          // The front of a card has nothing to scroll, so it claims the whole
          // gesture and moves on the first pixel. The back does scroll (bio,
          // interests, the action buttons), so it keeps vertical panning for
          // the browser and only horizontal movement is ours.
          touchAction: canInteract ? (card.isFlipped ? 'pan-y' : 'none') : undefined
        }}
      >
        {/* Verdict stamps — only the top card can be decided, so only it shows them */}
        {canInteract && (
          <>
            <SwipeVerdictStamp $variant="keep" style={{ opacity: keepStampOpacity }}>
              Keep
            </SwipeVerdictStamp>
            <SwipeVerdictStamp $variant="pass" style={{ opacity: passStampOpacity }}>
              Pass
            </SwipeVerdictStamp>
          </>
        )}

        {/* Conditional rendering - only show the active face */}
        {!card.isFlipped ? (
          /* Front Face (Teaser) with suit symbols */
          <CardFace key="front-face" $isFlipped={false}>
            {/* Playing Card Suit Corners - only on front */}
            <CardSuitCorner $suit={cardSuit.name} $color={cardSuit.color} $position="top-left">
              <CardSuitSymbol $color={cardSuit.color}>{cardSuit.symbol}</CardSuitSymbol>
            </CardSuitCorner>

            <CardSuitCorner $suit={cardSuit.name} $color={cardSuit.color} $position="bottom-right">
              <CardSuitSymbol $color={cardSuit.color}>{cardSuit.symbol}</CardSuitSymbol>
            </CardSuitCorner>

            <CardTeaser
              teaser={card.teaser}
              isActive={isTopCard}
              // The card immediately behind buffers ahead of its turn.
              shouldPreload={stackIndex <= 1}
            />
          </CardFace>
        ) : (
          /* Back Face (Revealed) */
          <CardFace 
            key="back-face" 
            $isFlipped={true}
            style={{
              pointerEvents: card.isFlipped ? 'auto' : 'none',
              userSelect: card.isFlipped ? 'auto' : 'none',
              touchAction: card.isFlipped ? 'auto' : 'none'
            }}
          >
            <CardRevealed
              revealed={card.revealed}
              user={card.user}
              onAction={(action) => {
                // Map action to swipe direction
                switch (action) {
                  case 'pass':
                    onSwipe('left');
                    break;
                  case 'like':
                    onSwipe('right');
                    break;
                  case 'superlike':
                    onSwipe('up');
                    break;
                  case 'save':
                    onSwipe('down');
                    break;
                }
              }}
              isInteractive={isTopCard}
              isAnimating={card.isAnimating}
              isVisible={card.isFlipped}
            />
          </CardFace>
        )}
      </SwipeCardContainer>
    </motion.div>
  );
};
